import { useState } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { AtlasLocation } from "@/hooks/useAtlasData";
import { Badge } from "@/components/ui/badge";
import { Users, Truck, MapPin } from "lucide-react";

type PopupView = "available" | "dayOff" | "tractors";

function createLocationIcon(availableCount: number, totalDrivers: number) {
  const ratio = totalDrivers > 0 ? availableCount / totalDrivers : 0;
  const color =
    totalDrivers === 0
      ? "hsl(215, 16%, 47%)"
      : ratio >= 0.7
        ? "hsl(142, 71%, 45%)"
        : ratio >= 0.4
          ? "hsl(38, 92%, 50%)"
          : "hsl(0, 72%, 51%)";

  return L.divIcon({
    className: "custom-location-marker",
    html: `<div style="
      width: 36px; height: 36px; border-radius: 50%;
      background: ${color}; border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 700; color: white;
      font-family: 'Inter', sans-serif;
    ">${availableCount}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -22],
  });
}

interface LocationMapMarkerProps {
  location: AtlasLocation;
}

export function LocationMapMarker({ location }: LocationMapMarkerProps) {
  const [view, setView] = useState<PopupView>("available");

  const available = location.drivers.filter((d) => !d.isDayOff);
  const dayOff = location.drivers.filter((d) => d.isDayOff);

  return (
    <Marker
      position={[location.lat, location.lng]}
      icon={createLocationIcon(available.length, location.drivers.length)}
    >
      <Popup className="location-popup" minWidth={260} maxWidth={320}>
        <div className="space-y-2.5 p-1">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <span className="font-bold text-sm">{location.name}</span>
          </div>

          {/* Metric cards — clickable to switch view */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <button
              onClick={() => setView("available")}
              className={`rounded p-1.5 transition-all outline-none ring-2 ${
                view === "available"
                  ? "ring-green-500 bg-green-500/20"
                  : "ring-transparent bg-green-500/10 hover:bg-green-500/20"
              }`}
            >
              <div className="text-sm font-bold text-green-600">{available.length}</div>
              <div className="text-[9px] text-green-600/80">Available</div>
            </button>
            <button
              onClick={() => setView("dayOff")}
              className={`rounded p-1.5 transition-all outline-none ring-2 ${
                view === "dayOff"
                  ? "ring-amber-500 bg-amber-500/20"
                  : "ring-transparent bg-amber-500/10 hover:bg-amber-500/20"
              }`}
            >
              <div className="text-sm font-bold text-amber-600">{dayOff.length}</div>
              <div className="text-[9px] text-amber-600/80">Day Off</div>
            </button>
            <button
              onClick={() => setView("tractors")}
              className={`rounded p-1.5 transition-all outline-none ring-2 ${
                view === "tractors"
                  ? "ring-blue-500 bg-blue-500/20"
                  : "ring-transparent bg-blue-500/10 hover:bg-blue-500/20"
              }`}
            >
              <div className="text-sm font-bold text-blue-600">{location.tractorCount}</div>
              <div className="text-[9px] text-blue-600/80">Tractors</div>
            </button>
          </div>

          {/* Available drivers list */}
          {view === "available" && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                <Users className="h-3 w-3" />
                Available Drivers
              </div>
              {available.length === 0 ? (
                <p className="text-[10px] text-muted-foreground italic pl-1">None available</p>
              ) : (
                <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto pr-0.5">
                  {available.map((d) => (
                    <Badge key={d.id} variant="secondary" className="text-[10px] h-5 shrink-0">
                      {d.name.split(" ")[0]} {d.name.split(" ").pop()?.[0]}.
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Day off drivers list */}
          {view === "dayOff" && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                <Users className="h-3 w-3" />
                Day Off
              </div>
              {dayOff.length === 0 ? (
                <p className="text-[10px] text-muted-foreground italic pl-1">None off today</p>
              ) : (
                <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto pr-0.5">
                  {dayOff.map((d) => (
                    <Badge key={d.id} variant="outline" className="text-[10px] h-5 opacity-70 shrink-0">
                      {d.name.split(" ")[0]} {d.name.split(" ").pop()?.[0]}.
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tractors list */}
          {view === "tractors" && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                <Truck className="h-3 w-3" />
                Tractors
              </div>
              {location.tractors.length === 0 ? (
                <p className="text-[10px] text-muted-foreground italic pl-1">No tractors assigned</p>
              ) : (
                <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto pr-0.5">
                  {location.tractors.map((t) => (
                    <Badge key={t.id} variant="outline" className="text-[10px] h-5 shrink-0">
                      {t.name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}
